// ============================================
// Firestore Adapters — NRS.01, NRS.08, NRS.09
//
// Wraps the client-side Firebase SDK to satisfy
// the injectable interfaces defined in the NRS
// service layer (functions/src/nurse/services/).
//
// Paths:
//   captainResults/{patientId}  — NRS.01 listener
//   sessions/{sessionId}/safetyProfile — NRS.08 persist
//   audit/{recordId} — NRS.09 audit trail
// ============================================

import {
  collection,
  doc,
  getDoc,
  setDoc,
  getDocs,
  query,
  where,
  onSnapshot,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import { db } from '../firebase';

// Re-export interface shapes so callers don't need to reach into functions/
import type { CaptainResultDocument } from '../../types/captainPipeline';
import type {
  ProcessedResultRecord,
  CorrectionDocument,
} from '../../../functions/src/nurse/types/captainResultListener';
import { signInAnonymously } from 'firebase/auth';
import { auth } from '../firebase';
import type {
  PersistedSessionState,
  NurseCompletionEvent,
  SessionResumeEvent,
} from '../../../functions/src/nurse/types/sessionStateHandoff';
import type {
  AuditRecord,
  AuditReviewStatus,
} from '../../../functions/src/nurse/types/conversationAudit';

// ── NRS.01: FirestoreProvider ──

export interface FirestoreProvider {
  onSnapshot(
    collectionPath: string,
    filter: { field: string; value: string },
    callback: (doc: CaptainResultDocument) => void,
    errorCallback: (error: Error) => void,
  ): () => void;
  /** XMO.09 D#3 c1: Subscribe to corrections subcollection for a session */
  onCorrectionsSnapshot(
    sessionId: string,
    callback: (correction: CorrectionDocument) => void,
    errorCallback: (error: Error) => void,
  ): () => void;
  getProcessedRecord(patientId: string, sessionId: string): Promise<ProcessedResultRecord | null>;
  setProcessedRecord(patientId: string, record: ProcessedResultRecord): Promise<void>;
  /** NRS.01 D#1 c2: Re-authenticate after auth token expiry */
  reauthenticate(): Promise<boolean>;
}

export function createFirestoreProvider(): FirestoreProvider {
  return {
    onSnapshot(
      collectionPath: string,
      filter: { field: string; value: string },
      callback: (document: CaptainResultDocument) => void,
      errorCallback: (error: Error) => void,
    ): () => void {
      const colRef = collection(db, collectionPath);
      const q = query(colRef, where(filter.field, '==', filter.value));
      const unsub = onSnapshot(
        q,
        (snapshot) => {
          for (const change of snapshot.docChanges()) {
            if (change.type === 'added' || change.type === 'modified') {
              callback(change.doc.data() as CaptainResultDocument);
            }
          }
        },
        (error) => errorCallback(error),
      );
      return unsub;
    },

    /**
     * XMO.09 D#3 c1: Subscribe to captain_results/{sessionId}/corrections subcollection.
     * Each new/modified correction document is emitted to the callback individually.
     */
    onCorrectionsSnapshot(
      sessionId: string,
      callback: (correction: CorrectionDocument) => void,
      errorCallback: (error: Error) => void,
    ): () => void {
      const colRef = collection(db, 'captain_results', sessionId, 'corrections');
      const unsub = onSnapshot(
        colRef,
        (snapshot) => {
          for (const change of snapshot.docChanges()) {
            if (change.type === 'added' || change.type === 'modified') {
              const data = change.doc.data();
              callback({
                correctionId: change.doc.id,
                sessionId,
                biomarkerName: data.biomarkerName as string,
                correctedValue: data.correctedValue as number,
                correctedUnit: data.correctedUnit as string,
                source: data.source as string,
                createdAt: data.createdAt instanceof Date ? data.createdAt : new Date(data.createdAt),
                notes: data.notes as string | undefined,
              });
            }
          }
        },
        (error) => errorCallback(error),
      );
      return unsub;
    },

    async getProcessedRecord(patientId: string, sessionId: string): Promise<ProcessedResultRecord | null> {
      const docRef = doc(db, 'processedResults', `${patientId}_${sessionId}`);
      const snap = await getDoc(docRef);
      if (!snap.exists()) return null;
      return snap.data() as ProcessedResultRecord;
    },

    async setProcessedRecord(patientId: string, record: ProcessedResultRecord): Promise<void> {
      const docRef = doc(db, 'processedResults', `${patientId}_${record.sessionId}`);
      await setDoc(docRef, record);
    },

    /** NRS.01 D#1 c2: Re-authenticate on token expiry via anonymous sign-in */
    async reauthenticate(): Promise<boolean> {
      try {
        await signInAnonymously(auth);
        return true;
      } catch (err) {
        console.error('[NRS.01] Re-authentication failed:', err);
        return false;
      }
    },
  };
}

// ── NRS.08: SessionFirestoreProvider ──

export interface SessionFirestoreProvider {
  atomicPersist(sessionId: string, state: PersistedSessionState): Promise<void>;
  writeEvent(path: string, event: NurseCompletionEvent | SessionResumeEvent): Promise<void>;
  getLastActiveAt(sessionId: string): Promise<Date | null>;
  setLastActiveAt(sessionId: string, timestamp: Date): Promise<void>;
}

export function createSessionFirestoreProvider(): SessionFirestoreProvider {
  return {
    async atomicPersist(sessionId: string, state: PersistedSessionState): Promise<void> {
      const docRef = doc(db, 'sessions', sessionId, 'safetyProfile', 'current');
      await setDoc(docRef, {
        ...state,
        updatedAt: serverTimestamp(),
      });
    },

    async writeEvent(path: string, event: NurseCompletionEvent | SessionResumeEvent): Promise<void> {
      // Path format: sessions/{sessionId}/events/{eventType}
      const parts = path.replace(/\{[^}]+\}/g, '').split('/').filter(Boolean);
      // Use the actual session from the event
      const sessionId = 'sessionId' in event ? event.sessionId : '';
      const eventType = parts[parts.length - 1] || 'event';
      const docRef = doc(db, 'sessions', sessionId, 'events', eventType);
      await setDoc(docRef, event);
    },

    async getLastActiveAt(sessionId: string): Promise<Date | null> {
      const docRef = doc(db, 'sessions', sessionId, 'safetyProfile', 'current');
      const snap = await getDoc(docRef);
      if (!snap.exists()) return null;
      const data = snap.data();
      return data.lastActiveAt ? new Date(data.lastActiveAt) : null;
    },

    async setLastActiveAt(sessionId: string, timestamp: Date): Promise<void> {
      const docRef = doc(db, 'sessions', sessionId, 'safetyProfile', 'current');
      await updateDoc(docRef, { lastActiveAt: timestamp.toISOString() });
    },
  };
}

// ── NRS.09: AuditFirestoreProvider ──

export interface AuditFirestoreProvider {
  writeAuditRecord(record: AuditRecord): Promise<void>;
  getAuditRecords(sessionId: string): Promise<AuditRecord[]>;
  getPendingReviews(): Promise<AuditRecord[]>;
  updateReviewStatus(
    recordId: string,
    status: AuditReviewStatus,
    verdict?: string,
    notes?: string,
  ): Promise<void>;
  writeProvenanceChain(chainId: string, data: Record<string, unknown>): Promise<void>;
}

export function createAuditFirestoreProvider(): AuditFirestoreProvider {
  return {
    async writeAuditRecord(record: AuditRecord): Promise<void> {
      const docRef = doc(db, 'auditRecords', record.recordId);
      await setDoc(docRef, record);
    },

    async getAuditRecords(sessionId: string): Promise<AuditRecord[]> {
      const colRef = collection(db, 'auditRecords');
      const q = query(colRef, where('sessionId', '==', sessionId));
      const snap = await getDocs(q);
      return snap.docs.map(d => d.data() as AuditRecord);
    },

    async getPendingReviews(): Promise<AuditRecord[]> {
      const colRef = collection(db, 'auditRecords');
      const q = query(colRef, where('reviewStatus', '==', 'pending'));
      const snap = await getDocs(q);
      return snap.docs.map(d => d.data() as AuditRecord);
    },

    async updateReviewStatus(
      recordId: string,
      status: AuditReviewStatus,
      verdict?: string,
      notes?: string,
    ): Promise<void> {
      const docRef = doc(db, 'auditRecords', recordId);
      const update: Record<string, unknown> = { reviewStatus: status };
      if (verdict !== undefined) update.verdict = verdict;
      if (notes !== undefined) update.notes = notes;
      await updateDoc(docRef, update);
    },

    async writeProvenanceChain(chainId: string, data: Record<string, unknown>): Promise<void> {
      const docRef = doc(db, 'provenanceChains', chainId);
      await setDoc(docRef, data);
    },
  };
}
